import { type MouseEvent } from 'react';
import { styled } from 'goober';

import { Z_INDEX_PROJECTS_TABLE_HEAD } from '@/theme/z-index';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import type {
  IAlign,
  IHeaderCell,
  ITableSort,
  IVerticalAlign,
} from '@/ui/table/table.types';
import { resolveResponsiveValue } from '@/theme/breakpoints';

export const TableHeaderCell = <T,>({
  cell,
  sort,
  sticky = true,
}: {
  cell: IHeaderCell<T>;
  sort?: ITableSort<T>;
  sticky?: boolean;
}) => {
  const onHeaderClick = (
    event: MouseEvent<HTMLDivElement>,
    cell: IHeaderCell<T>,
  ) => {
    const target = event.target as HTMLDivElement;

    if (target.closest('[data-name="select-all-checkbox"]')) {
      return;
    }

    if (!sort || !cell.sortKey) return;

    if (sort.sort === cell.sortKey) {
      sort.onSort({
        direction: sort.direction === 'desc' ? 'asc' : 'desc',
        sort: cell.sortKey as T,
      });

      return;
    }

    sort.onSort({
      direction: cell.defaultSortDirection ?? 'desc',
      sort: cell.sortKey as T,
    });
  };

  const isSortActive = sort?.sort === cell.sortKey && cell.withSort !== false;

  return (
    <Td
      key={cell.columnKey}
      align={cell.align}
      width={resolveResponsiveValue(cell.width)}
      padding={cell.padding}
      bgColor={cell.bgColor}
      sticky={sticky}
      colSpan={cell.colSpan}
      minWidth={resolveResponsiveValue(cell.minWidth)}
      maxWidth={resolveResponsiveValue(cell.maxWidth)}
    >
      <Cell
        height={cell.height}
        name="table-header-cell"
        gap="8px"
        isClickable={Boolean(sort) && Boolean(cell.sortKey)}
        onClick={event => onHeaderClick(event, cell)}
        alignItems="center"
        justify={
          cell.align === 'right'
            ? 'flex-end'
            : cell.align === 'center'
              ? 'center'
              : 'flex-start'
        }
      >
        {!cell.hideLabel && (
          <>
            {cell.checkboxCount !== undefined ? (
              <>
                {cell.value} ({cell.checkboxCount})
              </>
            ) : (
              cell.value
            )}
            {!!sort && !!cell.sortKey && (
              <SortArrow
                name="sort-arrow-box"
                sortDirection={
                  sort.direction === 'asc' && sort.sort === cell.sortKey
                    ? 'asc'
                    : 'desc'
                }
              >
                <Icon
                  icon={isSortActive ? 'ArrowUpWithBg' : 'SortArrowUpBlue'}
                />
              </SortArrow>
            )}
          </>
        )}
        {cell.checkboxActions}
      </Cell>
    </Td>
  );
};

const Td = styled('td')<{
  align?: IAlign;
  verticalAlign?: IVerticalAlign;
  width?: string;
  breakWord?: boolean;
  padding?: string;
  rowPadding?: string;
  bgColor?: string;
  sticky?: boolean;
  minWidth?: string;
  maxWidth?: string;
}>`
  ${({ sticky }) =>
    sticky &&
    `
    position: sticky;
    top: 0;
`};

  z-index: ${Z_INDEX_PROJECTS_TABLE_HEAD};

  ${({ padding }) => `padding: ${padding ? padding : '0.75em 1em'};`}
  ${({ align = 'left' }) => `text-align: ${align};`}
  ${({ width }) => Boolean(width) && `width: ${width};`}
  ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}

  /* Responsive text handling */
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  ${({ rowPadding }) =>
    rowPadding &&
    `
    &:first-child {
      padding-left: 60px;
    }

    &:last-child {
      padding-right: 60px;
    }
  `};

  text-align: ${({ align = 'left' }) => align};
  ${({ width }) => !!width && `width: ${width};`}
  ${({ breakWord }) => !!breakWord && 'word-break: break-word;'}
  background-color: ${({ theme, bgColor }) =>
    bgColor ?? theme.colors.basics.white};
`;

const Cell = styled(Flexbox)<{
  isClickable?: boolean;
}>`
  ${({ isClickable }) => Boolean(isClickable) && 'cursor: pointer'};
  ${({ theme }) => theme.typography.companyInfo.tableHeader}
  text-wrap: nowrap;
`;

const SortArrow = styled(Flexbox)<{ sortDirection: 'asc' | 'desc' }>`
  ${({ sortDirection }) =>
    sortDirection === 'desc' && 'transform: rotate3d(0, 0, 1, 180deg);'}
`;
